import React from 'react';
import './FundUs.css';

/* ── Traction highlights (real, current) ── */
const traction = [
  { metric: '4', label: 'U.S. Provisional Patents', sub: '63/939,190 · 63/962,385 · 63/988,485 · 64/034,536' },
  { metric: '2', label: 'FDA Comments Filed', sub: 'Public docket record' },
  { metric: '3', label: 'Corporate Entities', sub: 'NC parent, NN subsidiary, DH subsidiary' },
  { metric: '34', label: 'Gated Heads', sub: 'Pharmacokinetic neurochemical cascade' },
  { metric: 'Live', label: 'Aegis Product', sub: 'Three-prong PTSD monitoring, wellness.nexusneurodata.com' },
  { metric: 'v1.0', label: 'Android APK', sub: 'HIPAA-hardened, TWA-ready, Play Store queued' },
];

/* ── The Ask, per entity ── */
const asks = [
  {
    entity: 'Nexus Concordat, Inc.',
    role: 'Parent · IP Vault',
    posture: 'Closed cap table forever',
    accepts: 'Nothing at the equity layer. Community donations to the compute + server fund only.',
    color: 'nc',
  },
  {
    entity: 'NeXus Neuroinformatics, Inc.',
    role: 'Healthcare Operating Subsidiary',
    posture: 'Open cap table',
    accepts: 'Strategic capital for the FDA / SaMD lane. Clinical pilots. Hospital + academic partnerships. Biomarker qualification collaborators.',
    color: 'nn',
  },
  {
    entity: 'Dynamic Hallucination, Inc.',
    role: 'Non-Healthcare Operating Subsidiary',
    posture: 'Open cap table',
    accepts: 'Creative + entertainment strategic capital. Publishing partnerships. Licensing conversations. Non-clinical B2B.',
    color: 'dh',
  },
];

/* ── Community Donors (compute + server fund, no equity) ── */
const donors = [
  'Evelyn Williamson',
  'Dustin Reiser',
  'Jason Alba',
];

function FundUs() {
  return (
    <section className="fund-us">
      <div className="fund-inner">

        {/* Deck cover */}
        <div className="fund-headline reveal">
          <h2>The Substrate Is Built.<br />The Family Is Structured.<br />Here Is Where You Come In.</h2>
          <p className="fund-lead">
            Nexus Concordat, Inc. is the parent holding company for a three-entity family
            organized around one non-negotiable boundary: <strong>healthcare vs. everything else</strong>.
            Four U.S. provisional patents at the parent. Two subsidiaries carrying the applied work.
            Each entity accepts a different kind of support at a different layer of the stack.
          </p>
          <p className="fund-lead">
            This is where the deck opens. What you see below is what is actually live, what is patented,
            and where investment or partnership fits inside a structure built to keep the substrate sovereign.
          </p>
        </div>

        {/* Problem + solution, compact */}
        <div className="fund-block reveal">
          <h3>The Problem</h3>
          <p className="fund-lead">
            Standard clinical AI cannot show its reasoning until after the decision is already made. In regulated
            environments, that post-hoc explanation is not evidence. It is a story told about a decision the
            operator cannot audit at the moment it happens. Healthcare deployment stalls at that gap.
          </p>
          <h3>Our Solution</h3>
          <p className="fund-lead">
            A Neurochemical Language Model with a 34-head pharmacokinetic cascade, hash-verifiable at every
            inference step. Every response ships with a SHA-256 state hash and a citation chain. The audit trail
            is not attached to the answer. The audit trail <em>is</em> the pathway that produced it.
          </p>
        </div>

        {/* Traction grid */}
        <div className="fund-block reveal">
          <h3>Traction · What Is Real Right Now</h3>
          <div className="fund-traction-grid">
            {traction.map((t, i) => (
              <div className="fund-traction-card" key={i}>
                <div className="fund-traction-metric">{t.metric}</div>
                <div className="fund-traction-label">{t.label}</div>
                <div className="fund-traction-sub">{t.sub}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Corporate structure recap with per-entity asks */}
        <div className="fund-block reveal">
          <h3>The Ask · By Entity</h3>
          <p className="fund-lead">
            Nexus Concordat, Inc. never takes external capital. That posture protects the patents and the substrate.
            Every entity that operates against the market lives one layer below the parent and accepts investment
            on its own cap table. Match the entity to the conversation you want to have.
          </p>
          <div className="fund-asks-grid">
            {asks.map((a, i) => (
              <div className={`fund-ask-card fund-ask-${a.color}`} key={i}>
                <div className="fund-ask-entity">{a.entity}</div>
                <div className="fund-ask-role">{a.role}</div>
                <div className="fund-ask-posture">{a.posture}</div>
                <p className="fund-ask-accepts">{a.accepts}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact + CTA */}
        <div className="fund-block fund-cta-block reveal" id="talk">
          <h3>Open A Conversation</h3>
          <p className="fund-lead">
            Investors, strategic partners, clinical collaborators, and press: reach the parent office by email.
            Requests routed to the appropriate subsidiary based on the conversation.
          </p>
          <div className="fund-donate-buttons">
            <a
              href="mailto:contact@nexusconcordat.com"
              className="fund-donate-btn"
            >
              contact@nexusconcordat.com
            </a>
          </div>
          <p className="fund-cta-note">
            For the community compute + server fund at the parent level (no equity), see the donor wall below.
            Every name on that wall backed the substrate before the pitch deck existed.
          </p>
          <div className="fund-entity">
            Nexus Concordat, Inc. &middot; EIN 39-2932385 &middot; SAM.gov UEI WJ3SQQ24JHR4
          </div>
        </div>

        {/* Small community donor wall at the bottom */}
        <div className="fund-donors reveal">
          <h3>Community Compute Fund · Names On The Wall</h3>
          <p className="fund-donors-sub">
            Contributions to the parent for infrastructure, compute, and server costs. No equity, no amounts,
            no receipts. Just names, in the order they showed up.
          </p>
          {donors.length === 0 ? (
            <div className="fund-donors-empty">
              <p>This wall is waiting for its first name.</p>
            </div>
          ) : (
            <div className="fund-donors-wall">
              {donors.map((name, i) => (
                <span className="fund-donor-name" key={i}>{name}</span>
              ))}
            </div>
          )}
          <p className="fund-cta-note" style={{ marginTop: '1.5rem' }}>
            To add your name: donate via <a href="https://www.paypal.com/donate/?business=caelsereith%40aetherprotocols.com&currency_code=USD" target="_blank" rel="noopener noreferrer" style={{color: '#c9a84c'}}>PayPal</a> or <a href="https://www.gofundme.com/f/the-caelsereith-foundation" target="_blank" rel="noopener noreferrer" style={{color: '#c9a84c'}}>GoFundMe</a>, then email <a href="mailto:contact@nexusconcordat.com" style={{color: '#c9a84c'}}>contact@nexusconcordat.com</a> with the name you want listed. Or stay anonymous. Your call.
          </p>
        </div>

      </div>
    </section>
  );
}

export default FundUs;
